import React from 'react';
import { Route, IndexRedirect } from 'react-router';
import moment from 'moment';

import UserBar from '../components/presentational/Navigation/UserBar';
import SpaceBar from '../components/presentational/Navigation/SpaceBar';
import * as navigationActions from '../actions/navigation';
import MaterialsTab from '../components/presentational/ClassRoom/Material/MaterialsTab/MaterialsTab.container';
import Journey from '../components/presentational/ClassRoom/Journey/Journey.container';
import Content from '../components/presentational/Content';
import MobileView from '../components/presentational/Mobile/MobileView';
import OverlayLoader from '../components/containers/OverlayLoader';
import PostsTable from '../components/presentational/ClassRoom/Feed/PostsTable/PostsTable.container';
import UserProfile from '../components/presentational/UserProfile';
import Assignments from '../components/presentational/ClassRoom/Assignments';
import Submissions from '../components/presentational/ClassRoom/Submissions';
import EducatorArea from '../components/presentational/ClassRoom/Material/EducatorArea';
import EngagementPage from '../components/presentational/ClassRoom/EngagementPage';
import InboxGroupsMenu from '../components/presentational/InboxV2';
import OmniSearch from '../components/presentational/OmniSearch/OmniSearch.container';
import PostView from '../components/presentational/ClassRoom/Feed/Post/Post.container';
import ImportantPosts from '../components/presentational/ClassRoom/ImportantPosts/ImportantPosts.container';
import ScheduledPosts from '../components/presentational/ClassRoom/ScheduledPosts';
import SpaceSettings from '../components/presentational/ClassRoom/Settings/SpaceSettings';
import NoSpaces from '../components/presentational/NoSpaces';
import { fetchStartupData } from '../../core/actions/startup';
import { getStore } from '../store';
import conversations from '../store/conversations';
import groups from '../store/groups';
import materials from '../store/materials';
import notifications from '../store/notifications';
import reactions from '../store/reactions';
import assignments from '../store/assignments';
import fetchSignedCookie from '../../core/utils/filesSignedCookie';
import events from '../../core/actions/socket/events';
import { getCurrentUser } from '../../core/utils/currentUser';
import { FETCH_SIGNED_FILE_COOKIE } from '../constants/actions';
import getSignedCookieTimestamp from '../selectors/signedCookie';

const refreshCookieIntervalMS = 25 * 60 * 1000; // 25 minutes

const fetchFileCookie = ({ sessionToken, dispatch }) => async () => {
  await fetchSignedCookie({ sessionToken });
  dispatch({ type: FETCH_SIGNED_FILE_COOKIE });
};

const refetchCookieIfNeeded = ({ getState, fetchCookie }) => async () => {
  const timestamp = getSignedCookieTimestamp(getState());
  if (moment().diff(timestamp, 'ms') > refreshCookieIntervalMS || !timestamp) {
    await fetchCookie();
  }
};

const fetchInitialData = async () => {
  const store = getStore();
  const { data, user } = store.getState();
  if (!data.hasInitialData && !data.isFetchingInitialData) {
    const { sessionToken } = user.user;
    const fetchCookie = fetchFileCookie({
      sessionToken,
      dispatch: store.dispatch,
    });

    await fetchCookie();
    setInterval(() => fetchCookie(), refreshCookieIntervalMS);

    const refetchCookie = refetchCookieIfNeeded({
      getState: store.getState,
      fetchCookie,
    });

    const live = {
      conversations: conversations.registerLive,
      groups: groups.registerLive,
      materials: materials.registerLive,
      notifications: notifications.registerLive,
      reactions: reactions.registerLive,
      assignments: assignments.registerLive,
      events,
      reconnect: () => refetchCookie(),
    };

    return store.dispatch(
      fetchStartupData({
        conversations,
        live,
        files: {},
      })
    );
  }
  return null;
};

const onEnter = async (nextState, replace) => {
  if (!getCurrentUser()) {
    replace({
      pathname: '/signin/login',
      state: { nextPathname: nextState.location.pathname },
    });
    return;
  }
  await fetchInitialData();
  const { dispatch } = getStore();
  dispatch(
    navigationActions.decideAndLoadClassRoom(
      nextState.params.classId,
      nextState.location.search
    )
  );
};

const onNoSpacesEnter = async (nextState, replace) => {
  if (!getCurrentUser()) {
    replace({
      pathname: '/signin/login',
      state: { nextPathname: nextState.location.pathname },
    });
    return;
  }
  await fetchInitialData();
};

const DashboardRoute = (
  <Route path="">
    <Route path="dashboard" onEnter={onEnter} />
    <Route
      path="dashboard/:classId"
      components={{
        overlayLoader: OverlayLoader,
        content: Content,
        inboxGroupMenu: InboxGroupsMenu,
        omniSearch: OmniSearch,
        userBar: UserBar,
        spaceBar: SpaceBar,
        userProfile: UserProfile,
      }}
      onEnter={onEnter}
    >
      {/* legacy community path */}
      <Route path="feed" search="?tagId" component={PostsTable} />
      <Route path="feed" search="?profile" component={UserProfile} />
      <Route path="post/:id" component={PostView} />
      <Route path="important" component={ImportantPosts} />
      <Route path="scheduled" component={ScheduledPosts} />
      {/* legacy journey path */}
      <Route path="materials(/:sectionId)" component={MaterialsTab} />

      <IndexRedirect to="community" />
      <Route path="community">
        <IndexRedirect to="feed" />
        <Route path="feed" search="?tagId" component={PostsTable} />
        <Route path="feed" search="?profile" component={UserProfile} />
        <Route path="post/:id" component={PostView} />
        <Route path="important" component={ImportantPosts} />
        <Route path="scheduled" component={ScheduledPosts} />
      </Route>
      {/* new journey path */}
      <Route path="journey" component={Journey}>
        <IndexRedirect to="materials" />
        <Route path="materials(/:sectionId)" component={MaterialsTab} />
        <Route path="assignments" component={Assignments} />
        <Route
          path="assignments/:assignmentId/submissions"
          component={Submissions}
        />
        <Route path="educatorArea(/:sectionId)" component={EducatorArea} />
        <Route path="engagement" component={EngagementPage} />
      </Route>
      <Route path="settings" component={SpaceSettings}>
        <Route path="participants" component={SpaceSettings} />
        <Route path="groups" component={SpaceSettings} />
      </Route>
    </Route>
    <Route
      path="nospaces"
      components={{
        overlayLoader: OverlayLoader,
        content: NoSpaces,
        inboxGroupMenu: InboxGroupsMenu,
        omniSearch: OmniSearch,
        userBar: UserBar,
        userProfile: UserProfile,
      }}
      onEnter={onNoSpacesEnter}
    />
    <Route path="mobile" components={MobileView} />
  </Route>
);

export default DashboardRoute;
